const getEnvValue = (key, fallback = '') => {
  const value = process.env[key];
  return value === undefined || value === '' ? fallback : value;
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const normalizeBaseUrl = (url) => (url || '').replace(/\/+$/, '');
const buildApiUrl = (baseUrl, path) => `${normalizeBaseUrl(baseUrl)}${path}`;

const API_BASE_URL = getEnvValue('REACT_APP_API_BASE_URL', 'https://backend.thewhiteowl.ai');
// Must use a static process.env.* reference here — otherwise the login app URL becomes empty in CRA builds.
const LOGIN_APP_URL = normalizeBaseUrl(
  process.env.REACT_APP_LOGIN_APP_URL || 'https://app.thewhiteowl.ai'
);

// Mixpanel project tokens (same projects as zap-react / the app).
const MIXPANEL_TOKENS = {
  development: 'dea515feb4bd6d80f027307b46c38c84',
  staging: 'dea515feb4bd6d80f027307b46c38c84',
  production: 'dea515feb4bd6d80f027307b46c38c84',
};

const resolveMixpanelEnv = () => {
  if (typeof window === 'undefined') return 'development';
  const href = window.location.href || '';
  const host = window.location.hostname || '';
  if (host === 'localhost' || host === '127.0.0.1') return 'development';
  if (href.includes('staging.thewhiteowl.ai') || href.includes('staging')) return 'staging';
  if (
    href.includes('dev.thewhiteowl.ai')
    || href.includes('dev1.thewhiteowl.ai')
    || host.startsWith('dev.')
  ) {
    return 'development';
  }
  if (
    href.includes('thewhiteowl.ai')
    || href.includes('consultzoi.ai')
    || href.includes('app.thewhiteowl.ai')
  ) {
    return 'production';
  }
  return 'development';
};

const MIXPANEL_ENV = resolveMixpanelEnv();
// Static process.env access required for CRA to inline the value at build time.
const MIXPANEL_TOKEN =
  process.env.REACT_APP_MIXPANEL_TOKEN
  || MIXPANEL_TOKENS[MIXPANEL_ENV]
  || MIXPANEL_TOKENS.development;

export const APP_CONFIG = {
  appUrl: LOGIN_APP_URL,
  billingPath: '/Setting/billing',
  onboardingPath: '/onboarding',
  mixpanelToken: MIXPANEL_TOKEN,
  mixpanelEnv: MIXPANEL_ENV,
  getLoginRedirectUrl() {
    // Path only — login app resolves against its own origin (shorter than encoding a full URL).
    return `${this.appUrl}/?redirect=${this.billingPath}`;
  },
  getOnboardingRedirectUrl() {
    return `${this.appUrl}${this.onboardingPath}`;
  },
};

export const API_CONFIG = {
  registerUrl: buildApiUrl(API_BASE_URL, '/register/'),
  pricingRegisterUrl: buildApiUrl(API_BASE_URL, '/pricing-register/'),
  eapRegistrationUrl: buildApiUrl(API_BASE_URL, '/api/eap-registration/'),
  listFolderVideosUrl: buildApiUrl(API_BASE_URL, '/s3/list-folder-videos/'),
  quickSignupStartUrl: buildApiUrl(API_BASE_URL, '/quick-signup/start/'),
  quickSignupResumeUrl: buildApiUrl(API_BASE_URL, '/quick-signup/resume/'),
  quickSignupCompleteUrl: buildApiUrl(API_BASE_URL, '/quick-signup/complete/'),
  quickSignupOauthGoogleStartUrl: buildApiUrl(API_BASE_URL, '/quick-signup/oauth/google/start/'),
  quickSignupOauthMicrosoftStartUrl: buildApiUrl(API_BASE_URL, '/quick-signup/oauth/microsoft/start/'),
  quickSignupOauthCompleteUrl: buildApiUrl(API_BASE_URL, '/quick-signup/oauth/complete/'),
  paymentSuccessTokenUrl: buildApiUrl(API_BASE_URL, '/subscription/api/payment-success-token/'),
  salesOnboardingStartUrl: buildApiUrl(API_BASE_URL, '/sales-intelligence/onboarding/start/'),
  // Buy-now via OAuth (Razorpay Checkout.js order flow — distinct from the payment-link flow above).
  buyNowVerifyPaymentUrl: buildApiUrl(API_BASE_URL, '/subscription/api/buy-now-signup/verify-payment/'),
  buyNowPaymentSuccessTokenUrl: buildApiUrl(API_BASE_URL, '/subscription/api/payment-success-token/'),
  callbackRequestUrl: buildApiUrl(API_BASE_URL, '/api/contact-request/'),
};

export const QUICK_SIGNUP_STORAGE_KEY = 'wo_quick_signup';

export const PRICING_PLAN_IDS = {
  solo: toNumber(getEnvValue('REACT_APP_PLAN_ID_SOLO', '8'), 8),
  starter: toNumber(getEnvValue('REACT_APP_PLAN_ID_STARTER', '2'), 2),
  growth: toNumber(getEnvValue('REACT_APP_PLAN_ID_GROWTH', '3'), 3),
  soloAnnual: toNumber(getEnvValue('REACT_APP_PLAN_ID_SOLO_ANNUAL', '7'), 7),
  starterAnnual: toNumber(getEnvValue('REACT_APP_PLAN_ID_STARTER_ANNUAL', '5'), 5),
  growthAnnual: toNumber(getEnvValue('REACT_APP_PLAN_ID_GROWTH_ANNUAL', '6'), 6),
};

